import { useRequest } from 'features/request/useRequest';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import type { AnyObject } from 'types/AnyObject';

import type { IGetRowsParams } from 'ag-grid-community';
import { debounce } from 'debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { projectListGql as projectsListGql } from '../projectListFilterQueries';
import { getValuesFromSaved, getWhere, SEARCH_STRING } from '../projectListHelpers';

export const useProjectListData = () => {
    const [searchString, setSearchString] = useState('');
    const [queryWhere, setQueryWhere] = useState<any>();

    const { handleSaveFilterState } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: getValuesFromSaved({
                    setQueryWhere,
                    setSearchString,
                }),
                withFakeSetter: true,
            }),
            [],
        ),
    );

    const variables = useMemo(
        () => ({
            where: getWhere(queryWhere),
        }),
        [queryWhere],
    );

    const [projectsListData, , , request] = useRequest({
        requestId: 'projectsList',
        query: projectsListGql,
        variables,
        withEffect: false,
    });

    const changeOrderHandler = useCallback((params: IGetRowsParams) => {
        const { sortModel } = params;

        const { sort, colId } = sortModel?.[0] ?? {};

        if (colId && sort) {
            return [{ [colId]: sort.toUpperCase() }];
        }

        return { updatedAt: 'DESC' };
    }, []);

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'projectsList',
        requestFn: request,
        requestVariables: variables,
        customSortFn: changeOrderHandler,
    });

    useEffect(() => {
        if (projectsListData?.projects?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                projectsListData.projects.items ?? [],
                projectsListData.projects?.totalCount,
            );
        }
    }, [getRowParams, projectsListData?.projects?.items, projectsListData?.projects?.totalCount]);

    const handleSearch = useCallback(
        ({ value }) => {
            setQueryWhere((prevFilters: AnyObject) => ({
                ...prevFilters,
                searchString: value,
            }));
            handleSaveFilterState({
                key: SEARCH_STRING,
                newState: value,
            });
        },
        [handleSaveFilterState],
    );
    const debouncedSearch = useMemo(() => debounce(handleSearch, 500), [handleSearch]);
    const handleChange = useCallback(
        ({ value }) => {
            setSearchString(value);
            debouncedSearch({ value });
        },
        [debouncedSearch],
    );
    const handleClearSearch = useCallback(() => {
        if (searchString) {
            setSearchString('');
            handleSearch({ value: undefined });
        }
    }, [handleSearch, searchString]);

    const totalCount = projectsListData?.projects?.totalCount ?? 0;

    const fetchedItems = useMemo(() => {
        if (!getRowParams?.current?.endRow || !totalCount) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalCount) {
            return totalCount;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [totalCount, getRowParams?.current?.endRow]);

    return {
        searchString,
        handleChangeSearchString: handleChange,
        handleSearch,
        handleClearSearch,
        dataSource,
        changeOrderHandler,
        totalCount,
        fetchedItems,
        tableKey: queryWhere?.searchString ?? '',
    };
};
